import { useState, useEffect, useRef, useCallback } from 'react';
import { backdropClose } from '../utils/backdropClose';
import { Search, SlidersHorizontal, UserPlus, X, Filter, Eye, EyeOff, ShoppingCart, Dumbbell, Activity } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useUserContext } from '../contexts/UserContext';
import { useBranch } from '../contexts/BranchContext';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import EmptyState from '../components/ui/EmptyState';
import Pagination from '../components/ui/Pagination';
import AddMemberModal from '../components/AddMemberModal';
import EditMemberModal from '../components/EditMemberModal';
import PauseModal from '../components/PauseModal';
import MemberProgressGymModal from '../components/MemberProgressGymModal';
import MemberWorkoutPlanPanel from '../components/MemberWorkoutPlanPanel';
import MemberWorkoutLogPanel from '../components/MemberWorkoutLogPanel';
import CreatePurchaseModal from '../components/CreatePurchaseModal';

interface Membership {
  id: string;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  gym?: {
    name: string;
    displayName: string;
  };
  plan: {
    id: string;
    name: string;
    description: string | null;
    durationMonths: number | null;
    price: number | null;
    features?: any;
  };
  baseAmount?: number | null;
  taxAmount?: number | null;
  taxPercent?: number | null;
  totalAmount?: number | null;
  discountAmount?: number | null;
  discountPercent?: number | null;
  paymentStatus?: string | null;
  pendingAmount?: number | null;
  dueDate?: string | null;
}

interface Member {
  id: string;
  name: string | null;
  displayName: string | null;
  email: string | null;
  phoneNo: string | null;
  status: string | null;
  dateOfBirth?: string | null;
  gender?: string | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  address?: string | null;
  city?: string | null;
  pincode?: string | null;
  notes?: string | null;
  trialEndDate?: string | null;
  memberBranchAssignments?: Array<{
    branch: {
      id: string;
      name: string;
      displayName: string;
    };
  }>;
  memberships: Membership[];
  effectiveStatus?: string | null;
  /** Why a member with no plan running is not active: had one that ran out, or never had one. */
  membershipState?: 'expired' | 'no_plan' | null;
  /** Packs / credits still usable — shown beside the membership, never folded into it. */
  runningPacks?: RunningPack[];
  profilePhoto?: string | null;
}

interface RunningPack {
  name: string;
  /** null for a per-period plan (N a week), which refills */
  remaining: number | null;
  total: number | null;
  validTo: string | null;
}

/**
 * The Membership column: the member's plan, not a verdict on the person. Not
 * active says why — Expired (had a plan, it ran out: the renewal list) or No
 * plan (never had one).
 */
function membershipOf(member: { effectiveStatus?: string | null; status: string | null; membershipState?: string | null }): { key: string; label: string } {
  const key = member.effectiveStatus ?? member.status ?? 'inactive';
  if (key === 'inactive') {
    if (member.membershipState === 'expired') return { key: 'expired', label: 'Expired' };
    if (member.membershipState === 'no_plan') return { key: 'no_plan', label: 'No plan' };
    return { key, label: 'Inactive' };
  }
  const labels: Record<string, string> = { trial: 'Trial', lead: 'Lead', paused: 'Paused', active: 'Active', suspended: 'Suspended', deleted: 'Deleted' };
  return { key, label: labels[key] ?? key };
}

const packLabel = (p: RunningPack) => (p.remaining != null ? `${p.name} · ${p.remaining} left` : p.name);

/** "PT 10 · 6 left", "+1 more" — the packs a member can still use. */
function RunningPacksChip({ packs, compact = false }: { packs?: RunningPack[]; compact?: boolean }) {
  if (!packs?.length) return null;
  const more = packs.length - 1;
  return (
    <span
      title={packs.map(packLabel).join('\n')}
      className={`inline-flex items-center gap-1 rounded-md bg-indigo-50 text-indigo-700 font-semibold max-w-[180px] ${compact ? 'text-[10px] px-1.5 py-0.5' : 'text-[10px] px-2 py-0.5 mt-1 w-fit'}`}
    >
      <span className="truncate">{packLabel(packs[0])}</span>
      {more > 0 && <span className="flex-shrink-0 text-indigo-400">+{more}</span>}
    </span>
  );
}

// interface MembershipPlan {
//   id: string;
//   name: string;
//   description: string | null;
//   durationMonths: number | null;
//   price: number | null;
//   gym: {
//     name: string;
//     displayName: string;
//   };
// }

export default function MembersPage() {
  const { hasPermission, userContext, isOwner } = useUserContext();
  const { selectedBranchId, setSelectedBranchId } = useBranch();
  const [searchParams] = useSearchParams();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('filter') || '');
  const [renewalFilter, setRenewalFilter] = useState(searchParams.get('renewalFilter') || '');
  const [paymentOverdue, setPaymentOverdue] = useState(searchParams.get('paymentOverdue') === 'true');
  const [hasDue, setHasDue] = useState(searchParams.get('hasDue') === 'true');
  const [trainerFilter, setTrainerFilter] = useState(searchParams.get('trainerId') || '');
  const [trainers, setTrainers] = useState<Array<{ userId: string; name: string }>>([]);
  const [branches, setBranches] = useState<Array<{ id: string; name: string; displayName: string | null }>>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [isReadOnlyView, setIsReadOnlyView] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [memberToRestore, setMemberToRestore] = useState<Member | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [membershipToPause, setMembershipToPause] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedMemberIdForProgress, setSelectedMemberIdForProgress] = useState<string | null>(null);
  const [planPanelMember, setPlanPanelMember] = useState<{ id: string; name: string } | null>(null);
  const [logPanelMember, setLogPanelMember] = useState<{ id: string; name: string } | null>(null);
  const filterDrawerRef = useRef<HTMLDivElement>(null);
  const lastFetchedRef = useRef<string | null>(null);
  const [showFullContact, setShowFullContact] = useState(false);
  // Quick-add purchase from member row
  const [showQuickPurchaseModal, setShowQuickPurchaseModal] = useState(false);
  const [quickPurchaseMemberId, setQuickPurchaseMemberId] = useState('');
  const [quickPurchaseMemberName, setQuickPurchaseMemberName] = useState('');

  const maskPhoneNumber = (phone: string | null) => {
    if (!phone) return '-';
    if (showFullContact) return phone;
    if (phone.length <= 5) return '*****';
    return phone.slice(0, -5) + '*****';
  };

  const maskEmail = (email: string | null) => {
    if (!email) return null;
    if (showFullContact) return email;
    const [local, domain] = email.split('@');
    if (!domain) return '*****';
    if (local.length <= 2) return '*@' + domain;
    return local[0] + '***' + local[local.length - 1] + '@' + domain;
  };

  // Draft states for filters (only applied when "Apply" is clicked)
  const [draftStatusFilter, setDraftStatusFilter] = useState(statusFilter);
  const [draftRenewalFilter, setDraftRenewalFilter] = useState(renewalFilter);
  const [draftPaymentOverdue, setDraftPaymentOverdue] = useState(paymentOverdue);
  const [draftHasDue, setDraftHasDue] = useState(hasDue);
  const [draftTrainerFilter, setDraftTrainerFilter] = useState(trainerFilter);
  const [draftBranchFilter, setDraftBranchFilter] = useState(selectedBranchId || '');

  // Sync draft states when drawer opens
  useEffect(() => {
    if (showFiltersDrawer) {
      setDraftStatusFilter(statusFilter);
      setDraftRenewalFilter(renewalFilter);
      setDraftPaymentOverdue(paymentOverdue);
      setDraftHasDue(hasDue);
      setDraftTrainerFilter(trainerFilter);
      setDraftBranchFilter(selectedBranchId || '');
    }
  }, [showFiltersDrawer, statusFilter, renewalFilter, paymentOverdue, hasDue, trainerFilter, selectedBranchId]);

  // Fetch trainers once for the filter dropdown
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch(`${API_BASE_URL}/api/trainers?role=trainer&status=active`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        });
        if (!res.ok) return; // permission may be lacking; degrade silently
        const json = await res.json();
        if (cancelled) return;
        const list: Array<{ userId: string; name: string }> = (json.data || [])
          .map((t: any) => ({ userId: t.userId, name: t.displayName || t.name || '—' }))
          .filter((t: { userId: string }) => !!t.userId);
        // Dedupe by userId (a trainer can show up across multiple branches)
        const seen = new Set<string>();
        setTrainers(list.filter(t => seen.has(t.userId) ? false : (seen.add(t.userId), true)));
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch branches once for the branch filter dropdown (restricted to the user's
  // accessible branches for non-owners).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch(`${API_BASE_URL}/api/branches`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        });
        if (!res.ok) return;
        const json = await res.json();
        if (cancelled) return;
        const list = (json.data || json.branches || []).filter(
          (b: any) => (b.status ?? 'active') !== 'inactive'
        );
        const accessible = userContext?.accessibleBranches || [];
        setBranches(isOwner ? list : list.filter((b: any) => accessible.includes(b.id)));
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [isOwner, userContext?.accessibleBranches]);

  // Close filter drawer on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterDrawerRef.current && !filterDrawerRef.current.contains(event.target as Node)) {
        setShowFiltersDrawer(false);
      }
    }
    if (showFiltersDrawer) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFiltersDrawer]);

  const activeFilterCount = [
    statusFilter !== '' && statusFilter !== 'all',
    renewalFilter !== '',
    paymentOverdue,
    hasDue,
    trainerFilter !== '',
    !!selectedBranchId
  ].filter(Boolean).length;

  const canManageMembers = hasPermission('manage_members');
  const canViewMembers = hasPermission('view_members');

  // Fetch members and plans
  const fetchData = useCallback(async (updateMemberId?: string, silent = false) => {
    // Generate a unique key for the current request state
    const fetchKey = `${page}-${searchTerm}-${statusFilter}-${renewalFilter}-${paymentOverdue}-${hasDue}-${trainerFilter}-${selectedBranchId || ''}`;
    
    // Only skip if it's the SAME state and NOT a silent/forced refresh
    if (!silent && !updateMemberId && lastFetchedRef.current === fetchKey) {
      return;
    }

    try {
      if (!silent) setLoading(true);
      lastFetchedRef.current = fetchKey;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;

      // Fetch members with status filter if provided
      const url = new URL(`${apiUrl}/api/members`);
      url.searchParams.append('page', page.toString());
      url.searchParams.append('limit', limit.toString());

      if (statusFilter) {
        url.searchParams.append('statusFilter', statusFilter);
      }

      if (renewalFilter) {
        url.searchParams.append('renewalFilter', renewalFilter);
      }

      if (paymentOverdue) {
        url.searchParams.append('paymentOverdue', 'true');
      }

      if (hasDue) {
        url.searchParams.append('hasDue', 'true');
      }

      if (trainerFilter) {
        url.searchParams.append('trainerId', trainerFilter);
      }

      if (searchTerm) {
        url.searchParams.append('searchTerm', searchTerm);
      }

      if (selectedBranchId) {
        url.searchParams.append('branchId', selectedBranchId);
      }

      const membersResponse = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (membersResponse.ok) {
        const membersData = await membersResponse.json();
        const newMembers = membersData.data || [];
        setMembers(newMembers);
        setTotalCount(membersData.pagination?.total || 0);
        setTotalPages(membersData.pagination?.totalPages || 0);

        // If we need to update the currently editing member
        if (updateMemberId) {
          const updated = newMembers.find((m: Member) => m.id === updateMemberId);
          if (updated) {
            setEditingMember(updated);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load members data');
      lastFetchedRef.current = null; // Let it retry on failure
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, statusFilter, renewalFilter, paymentOverdue, hasDue, trainerFilter, limit, selectedBranchId]);

  const handleEditMember = (member: Member) => {
    setEditingMember(member);
    setIsReadOnlyView(false);
    setShowEditModal(true);
  };

  const handleMemberUpdated = (updatedMember?: Member) => {
    if (updatedMember) {
      // Immediate UI update
      setEditingMember(updatedMember);
      setMembers(prev => prev.map(m => m.id === updatedMember.id ? updatedMember : m));
    }
    fetchData(editingMember?.id, true); // Silent refresh in background
  };

  const handleDeleteMember = (member: Member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;

    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Not authenticated');
        return;
      }

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/members/${memberToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        toast.success('Member deleted successfully');
        setShowDeleteModal(false);
        setMemberToDelete(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to delete member');
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error('Failed to delete member');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreMember = (member: Member) => {
    setMemberToRestore(member);
    setShowRestoreModal(true);
  };

  const confirmRestore = async () => {
    if (!memberToRestore) return;

    setRestoring(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Not authenticated');
        return;
      }

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/members/${memberToRestore.id}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        toast.success('Member restored successfully');
        setShowRestoreModal(false);
        setMemberToRestore(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to restore member');
      }
    } catch (error) {
      console.error('Error restoring member:', error);
      toast.error('Failed to restore member');
    } finally {
      setRestoring(false);
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  useEffect(() => {
    if (searchInput === '' && searchTerm !== '') {
      setSearchTerm('');
      setPage(1);
    }
  }, [searchInput, searchTerm]);

  useEffect(() => {
    if (canViewMembers) {
      fetchData();
    }
  }, [canViewMembers, fetchData]);

  // Filter members based on search and status (now handled by backend, but keeping for safety if needed)
  const filteredMembers = members;

  if (!canViewMembers) {
    return (
      <EmptyState
        title="Access Denied"
        description="You don't have permission to view members."
        variant="error"
      />
    );
  }

  // Removed full-page loading to keep controls visible during refresh


  return (
    <div className="space-y-6">
      {/* Premium Header */}
      <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 rounded-full blur-3xl -mr-32 -mt-32 transition-colors group-hover:bg-blue-100/50"></div>
        <div className="relative">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none">Gym Members</h1>
          <p className="text-sm font-bold text-gray-400 mt-2 uppercase tracking-widest">Directory & Membership Lifecycle</p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 relative">
          <button
            onClick={() => setShowFiltersDrawer(true)}
            className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all border flex items-center justify-center gap-2 ${activeFilterCount > 0
              ? 'bg-blue-50 border-blue-200 text-blue-600'
              : 'bg-gray-50 border-gray-100 text-gray-500 hover:text-gray-700 hover:bg-white/40'
              }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>

          {canManageMembers && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-blue-100 flex items-center justify-center gap-2"
            >
              <UserPlus className="w-4 h-4" />
              <span>Add Member</span>
            </button>
          )}
        </div>
      </div>

      {/* Search Row */}
      <div className="flex items-center justify-between gap-2 bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setSearchTerm(e.target.value);
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search by name, email or phone..."
            className="block w-full pl-9 pr-10 py-2.5 border border-transparent rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all shadow-inner"
          />
          {searchInput && (
            <button
              onClick={() => {
                setSearchInput('');
                setSearchTerm('');
                setPage(1);
              }}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          onClick={handleSearch}
          className="px-6 py-3 bg-gray-900 text-[#AAD816] rounded-xl text-xs font-black uppercase tracking-widest hover:bg-[#AAD816] hover:text-zinc-950 transition-all border border-zinc-800 active:scale-95 shadow-sm"
        >
          Search
        </button>
      </div>

      {/* Count + Reveal Toggle */}
      <div className="flex items-center justify-between -mt-2 mb-2 px-1">
        {totalCount > 0 ? (
          <span className="text-sm font-semibold text-gray-500">
            <span className="text-gray-900">{totalCount}</span>{' '}
            {activeFilterCount > 0 || searchTerm ? 'result' : 'member'}{totalCount !== 1 ? 's' : ''}
          </span>
        ) : (
          <span />
        )}
        <button
          onClick={() => setShowFullContact(!showFullContact)}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-blue-600 bg-white border border-blue-100 rounded-xl hover:bg-blue-50 transition-all shadow-sm active:scale-95"
          title={showFullContact ? "Hide contact info" : "Reveal contact info"}
        >
          {showFullContact ? (
            <>
              <EyeOff className="w-4 h-4" />
              <span>Hide Contact</span>
            </>
          ) : (
            <>
              <Eye className="w-4 h-4" />
              <span>Show Contact</span>
            </>
          )}
        </button>
      </div>

      {/* Filter Drawer / Sidebar Overlay */}
      {showFiltersDrawer && (
        <div className="fixed inset-0 z-[60] flex justify-end overflow-hidden bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div
            ref={filterDrawerRef}
            className="w-full max-w-[320px] bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
          >
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-900" />
                <h2 className="text-xl font-bold text-gray-900">Filters</h2>
              </div>
              <button
                onClick={() => setShowFiltersDrawer(false)}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                title="Close"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              {/* Status Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Membership</h3>
                <div className="grid grid-cols-2 gap-2">
                  {['all', 'lead', 'trial', 'active', 'paused', 'expired', 'no_plan', 'suspended', 'deleted'].map((status) => (
                    <button
                      key={status}
                      onClick={() => setDraftStatusFilter(status)}
                      className={`px-3 py-2 text-xs font-bold rounded-xl transition-all border capitalize ${draftStatusFilter === status
                        ? 'bg-blue-600 border-blue-600 text-white shadow-md'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-blue-400'
                        }`}
                    >
                      {status === 'no_plan' ? 'No plan' : status}
                    </button>
                  ))}
                </div>
              </div>

              {/* Renewal Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Renewals Due In</h3>
                <div className="flex gap-2">
                  {['', '2', '5'].map((days) => (
                    <button
                      key={days}
                      onClick={() => setDraftRenewalFilter(days)}
                      className={`flex-1 px-3 py-2 text-xs font-bold rounded-xl transition-all border ${draftRenewalFilter === days
                        ? 'bg-orange-500 border-orange-500 text-white shadow-md'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-orange-400'
                        }`}
                    >
                      {days === '' ? 'All' : `${days} Days`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Overdue Section */}
              <div className="space-y-4 pt-4 border-t border-gray-50">
                <label className="flex items-center justify-between cursor-pointer group">
                  <div className="space-y-0.5">
                    <span className="text-sm font-bold text-gray-900">Payment Overdue</span>
                    <p className="text-xs text-gray-500">Members with a payment past its due date</p>
                  </div>
                  <div className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${draftPaymentOverdue ? 'bg-orange-500' : 'bg-gray-200'}`}>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={draftPaymentOverdue}
                      onChange={(e) => setDraftPaymentOverdue(e.target.checked)}
                    />
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${draftPaymentOverdue ? 'translate-x-5' : 'translate-x-0'}`} />
                  </div>
                </label>

                {/* Payment Due Filter */}
                <label className="flex items-center justify-between cursor-pointer group">
                  <div className="space-y-0.5">
                    <span className="text-sm font-bold text-gray-900">Payment Due</span>
                    <p className="text-xs text-gray-500">Members with pending/due payments</p>
                  </div>
                  <div className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${draftHasDue ? 'bg-amber-500' : 'bg-gray-200'}`}>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={draftHasDue}
                      onChange={(e) => setDraftHasDue(e.target.checked)}
                    />
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${draftHasDue ? 'translate-x-5' : 'translate-x-0'}`} />
                  </div>
                </label>
              </div>

              {/* Trainer Filter */}
              {trainers.length > 0 && (
                <div className="space-y-4 pt-4 border-t border-gray-50">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Assigned Trainer</h3>
                  <select
                    value={draftTrainerFilter}
                    onChange={(e) => setDraftTrainerFilter(e.target.value)}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm font-medium text-gray-700"
                  >
                    <option value="">Any trainer</option>
                    {trainers.map(t => (
                      <option key={t.userId} value={t.userId}>{t.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">Shows members currently assigned to the selected trainer.</p>
                </div>
              )}

              {/* Branch Filter */}
              {branches.length > 1 && (
                <div className="space-y-4 pt-4 border-t border-gray-50">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Branch</h3>
                  <select
                    value={draftBranchFilter}
                    onChange={(e) => setDraftBranchFilter(e.target.value)}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm font-medium text-gray-700"
                  >
                    <option value="">All branches</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.displayName || b.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">Shows members whose home branch matches. Also switches the branch scope shown in the top bar.</p>
                </div>
              )}
            </div>

            <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => {
                  setStatusFilter('all');
                  setRenewalFilter('');
                  setPaymentOverdue(false);
                  setHasDue(false);
                  setTrainerFilter('');
                  setSelectedBranchId(null);
                  setDraftBranchFilter('');
                  setPage(1);
                  setShowFiltersDrawer(false);
                }}
                className="flex-1 py-3 text-sm font-bold text-gray-600 hover:text-gray-900 transition-colors"
                title="Reset all filters"
              >
                Reset All
              </button>
              <button
                onClick={() => {
                  setStatusFilter(draftStatusFilter);
                  setRenewalFilter(draftRenewalFilter);
                  setPaymentOverdue(draftPaymentOverdue);
                  setHasDue(draftHasDue);
                  setTrainerFilter(draftTrainerFilter);
                  setSelectedBranchId(draftBranchFilter || null);
                  setPage(1);
                  setShowFiltersDrawer(false);
                }}
                className="flex-1 py-3 bg-blue-600 text-white text-sm font-bold rounded-xl shadow-lg shadow-blue-200 active:scale-95 transition-all"
                title="Apply filters"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Members List Section */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white/50 backdrop-blur-sm rounded-2xl border border-gray-100 min-h-[400px]">
          <LoadingSpinner />
          <p className="mt-4 text-gray-500 font-medium animate-pulse">Fetching members...</p>
        </div>
      ) : filteredMembers.length === 0 ? (
        <EmptyState
          icon="👥"
          title="No members found"
          description={searchTerm || statusFilter || renewalFilter ? "Try adjusting your filters to find what you're looking for." : "Get started by adding your first member."}
          action={searchTerm || statusFilter || renewalFilter ? (
            <button
              onClick={() => {
                setSearchTerm('');
                setSearchInput('');
                setStatusFilter('');
                setRenewalFilter('');
                setPaymentOverdue(false);
                setPage(1);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear all filters
            </button>
          ) : undefined}
        />
      ) : (
        <div className="space-y-4">
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Membership</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                    {canManageMembers && (
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredMembers.map((member) => {
                    const activeMembership = member.memberships.find(m => (m.status === 'active' || m.status === 'paused') && (!m.endDate || new Date(m.endDate) >= new Date()));
                    const daysUntilExpiry = activeMembership?.endDate
                      ? Math.ceil((new Date(activeMembership.endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                      : null;

                    return (
                      <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div 
                              className={`flex-shrink-0 h-10 w-10 ${member.profilePhoto ? 'cursor-zoom-in' : ''}`}
                              onClick={(e) => {
                                if (member.profilePhoto) {
                                  e.stopPropagation();
                                  setViewingPhoto(member.profilePhoto);
                                }
                              }}
                            >
                              {member.profilePhoto ? (
                                <img 
                                  src={member.profilePhoto} 
                                  alt={member.displayName || member.name || 'Member'} 
                                  className="h-10 w-10 rounded-full object-cover border border-gray-200"
                                />
                              ) : (
                                <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                                  {(member.displayName || member.name || 'M').charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {member.displayName || member.name || 'Unknown'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 font-medium tabular-nums">{maskPhoneNumber(member.phoneNo)}</div>
                          {member.email && (
                            <div className="text-sm text-gray-500">{maskEmail(member.email)}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const trialDaysLeft = member.trialEndDate
                              ? Math.ceil((new Date(member.trialEndDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                              : null;

                            const { key: displayStatus, label: displayLabel } = membershipOf(member);
                            return (
                              <div className="flex flex-col">
                                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full w-fit ${displayStatus === 'trial'
                                  ? 'bg-green-100 text-green-800'
                                  : displayStatus === 'lead'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : displayStatus === 'paused'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : displayStatus === 'active'
                                        ? 'bg-blue-100 text-blue-800'
                                        : displayStatus === 'suspended'
                                          ? 'bg-red-100 text-red-800'
                                          : displayStatus === 'deleted'
                                            ? 'bg-gray-200 text-gray-600'
                                            : 'bg-gray-100 text-gray-800'
                                  }`}>
                                  {displayLabel}
                                </span>
                                {displayStatus === 'trial' && trialDaysLeft !== null && (
                                  <span className={`text-[10px] mt-1 font-medium ${trialDaysLeft < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {trialDaysLeft >= 0 ? `${trialDaysLeft} days left` : 'Trial Expired'}
                                  </span>
                                )}
                                <RunningPacksChip packs={member.runningPacks} />
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {activeMembership ? (
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="text-sm font-medium text-gray-900">{activeMembership.plan.name}</div>
                                {activeMembership.status === 'paused' && (
                                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded text-[10px] font-semibold">PAUSED</span>
                                )}
                                {(() => {
                                  const ps = activeMembership.paymentStatus;
                                  const hasDueBalance = ps === 'due' || ps === 'partial';
                                  const isOverdue = hasDueBalance && activeMembership.dueDate && new Date(activeMembership.dueDate) < new Date();
                                  if (isOverdue) return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">OVERDUE</span>;
                                  if (hasDueBalance) return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold">DUE ₹{Number(activeMembership.pendingAmount || 0).toFixed(0)}</span>;
                                  return null;
                                })()}
                              </div>
                              {daysUntilExpiry !== null && (
                                <div className={`text-sm ${daysUntilExpiry < 7 ? 'text-red-600 font-medium' : daysUntilExpiry < 30 ? 'text-yellow-600' : 'text-gray-500'}`}>
                                  {daysUntilExpiry > 0 ? `${daysUntilExpiry} days left` : 'Expired'}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">
                              {canManageMembers ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setQuickPurchaseMemberId(member.id);
                                    setQuickPurchaseMemberName(member.displayName || member.name || '');
                                    setShowQuickPurchaseModal(true);
                                  }}
                                  className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 font-bold hover:bg-indigo-50 px-2 py-1 rounded-lg transition-colors"
                                >
                                  <ShoppingCart className="w-3.5 h-3.5" /> Add Purchase
                                </button>
                              ) : (
                                <span className="text-gray-400">No active plan</span>
                              )}
                            </span>
                          )}
                        </td>
                        {canManageMembers && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              {/* Pause action removed from member list - managed via membership tab */}
                              {member.status === 'deleted' ? (
                                <>
                                  <button
                                    onClick={() => {
                                      setEditingMember(member);
                                      setIsReadOnlyView(true);
                                      setShowEditModal(true);
                                    }}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="View History"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleRestoreMember(member)}
                                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    title="Restore Member"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPlanPanelMember({ id: member.id, name: member.name || member.phoneNo || 'Member' });
                                    }}
                                    className="p-2 text-lime-700 hover:bg-lime-50 rounded-lg transition-colors"
                                    title="Workout plan"
                                  >
                                    <Dumbbell className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setLogPanelMember({ id: member.id, name: member.name || member.phoneNo || 'Member' });
                                    }}
                                    className="p-2 text-lime-700 hover:bg-lime-50 rounded-lg transition-colors"
                                    title="Workouts logged"
                                  >
                                    <Activity className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      setSelectedMemberIdForProgress(member.id);
                                      setShowProgressModal(true);
                                    }}
                                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                    title="Progress Intelligence"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleEditMember(member)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Edit Details"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteMember(member)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View (Compact) */}
          <div className="md:hidden space-y-2">
            {filteredMembers.map((member) => {
              const activeMembership = member.memberships.find(m => (m.status === 'active' || m.status === 'paused') && (!m.endDate || new Date(m.endDate) >= new Date()));
              const daysUntilExpiry = activeMembership?.endDate
                ? Math.ceil((new Date(activeMembership.endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                : null;

              return (
                <div
                  key={member.id}
                  className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between gap-3 active:bg-gray-50 transition-colors"
                  onClick={() => handleEditMember(member)}
                >
                  <div 
                    className={`flex-shrink-0 cursor-pointer ${member.profilePhoto ? 'cursor-zoom-in' : ''}`}
                    onClick={(e) => {
                      if (member.profilePhoto) {
                        e.stopPropagation();
                        setViewingPhoto(member.profilePhoto);
                      }
                    }}
                  >
                    {member.profilePhoto ? (
                      <img 
                        src={member.profilePhoto} 
                        alt={member.displayName || member.name || 'Member'} 
                        className="h-10 w-10 rounded-full object-cover border border-gray-200 shadow-sm"
                      />
                    ) : (
                      <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm">
                        {(member.displayName || member.name || 'M').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-bold text-gray-900 text-sm truncate">{member.displayName || member.name}</div>
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md flex-shrink-0 ${(member.effectiveStatus ?? member.status) === 'trial' ? 'bg-green-100 text-green-800' :
                        (member.effectiveStatus ?? member.status) === 'lead' ? 'bg-yellow-100 text-yellow-800' :
                          (member.effectiveStatus ?? member.status) === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                            (member.effectiveStatus ?? member.status) === 'active' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-600'
                        }`}>
                        {(member.effectiveStatus ?? member.status) === 'active' ? 'ACT' : membershipOf(member).label}
                      </span>
                      <RunningPacksChip packs={member.runningPacks} compact />
                    </div>

                    <div className="text-xs text-gray-500 truncate mt-0.5 flex items-center gap-1.5">
                      {(member.effectiveStatus ?? member.status) === 'trial' ? (() => {
                        const trialDaysLeft = member.trialEndDate
                          ? Math.ceil((new Date(member.trialEndDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                          : null;
                        return (
                          <span className={trialDaysLeft !== null && trialDaysLeft < 0 ? 'text-red-600 font-bold' : 'text-green-600 font-medium'}>
                            {trialDaysLeft !== null ? (trialDaysLeft >= 0 ? `${trialDaysLeft} trial days left` : 'Trial') : 'Trial'}
                          </span>
                        );
                      })() : activeMembership ? (
                        <>
                          <span className="font-medium text-gray-700 truncate">{activeMembership.plan.name}</span>
                          {(() => {
                            const ps = activeMembership.paymentStatus;
                            const hasDueBalance = ps === 'due' || ps === 'partial';
                            const isOverdue = hasDueBalance && activeMembership.dueDate && new Date(activeMembership.dueDate) < new Date();
                            if (isOverdue) return <span className="text-red-600 font-bold text-[10px]">· OVERDUE</span>;
                            if (hasDueBalance) return <span className="text-amber-600 font-bold text-[10px]">· ₹{Number(activeMembership.pendingAmount || 0).toFixed(0)} due</span>;
                            return null;
                          })()}
                          <span className="text-gray-300">•</span>
                          {activeMembership.status === 'paused' ? (
                            <span className="text-yellow-600 font-bold">Paused</span>
                          ) : daysUntilExpiry !== null ? (
                            <span className={daysUntilExpiry < 7 ? 'text-red-600 font-bold' : daysUntilExpiry < 30 ? 'text-yellow-600 font-medium' : 'text-gray-500'}>
                              {daysUntilExpiry > 0 ? `${daysUntilExpiry}d left` : 'Expired'}
                            </span>
                          ) : null}
                        </>
                      ) : canManageMembers ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setQuickPurchaseMemberId(member.id);
                            setQuickPurchaseMemberName(member.displayName || member.name || '');
                            setShowQuickPurchaseModal(true);
                          }}
                          className="flex items-center gap-1 text-[10px] text-indigo-500 font-bold"
                        >
                          <ShoppingCart className="w-3 h-3" /> Add
                        </button>
                      ) : null}
                      {((member.effectiveStatus ?? member.status) === 'trial' || activeMembership) && (
                        <span className="text-gray-300">•</span>
                      )}
                      <span className="tabular-nums">{maskPhoneNumber(member.phoneNo)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  {canManageMembers && (
                    <div className="flex items-center gap-2">
                      {/* Edit button hidden on very small screens since row is clickable, but kept icon for affordance if needed. 
                           Actually making row clickable works best, maybe just Delete button? 
                           Let's keep both small icons for clarity. */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPlanPanelMember({ id: member.id, name: member.name || member.phoneNo || 'Member' });
                        }}
                        className="p-2 text-lime-700 hover:bg-lime-50 rounded-lg transition-colors"
                        title="Workout plan"
                      >
                        <Dumbbell className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setLogPanelMember({ id: member.id, name: member.name || member.phoneNo || 'Member' });
                        }}
                        className="p-2 text-lime-700 hover:bg-lime-50 rounded-lg transition-colors"
                        title="Workouts logged"
                      >
                        <Activity className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setSelectedMemberIdForProgress(member.id);
                          setShowProgressModal(true);
                        }}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Progress Intelligence"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 112-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteMember(member); }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <Pagination
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={limit}
            onPageChange={setPage}
          />
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {
        showDeleteModal && memberToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" {...backdropClose(() => setShowDeleteModal(false))}>
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">Delete Member</h3>
                  <p className="text-sm text-gray-500 mt-1">This action cannot be undone</p>
                </div>
              </div>
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to delete <span className="font-semibold">{memberToDelete.displayName || memberToDelete.name}</span>?
                  This will soft delete the member by marking them as deleted. You can restore them later if needed.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setMemberToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {deleting && (
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                  <span>{deleting ? 'Deleting...' : 'Delete Member'}</span>
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Restore Confirmation Modal */}
      {
        showRestoreModal && memberToRestore && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" {...backdropClose(() => setShowRestoreModal(false))}>
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">Restore Member</h3>
                  <p className="text-sm text-gray-500 mt-1">Bring this member back</p>
                </div>
              </div>
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to restore <span className="font-semibold">{memberToRestore.displayName || memberToRestore.name}</span>?
                  This will make the member active and restore their branch assignments.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRestoreModal(false);
                    setMemberToRestore(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={restoring}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRestore}
                  disabled={restoring}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {restoring && (
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                  <span>{restoring ? 'Restoring...' : 'Restore Member'}</span>
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Add Member Modal */}
      <AddMemberModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={async (memberId) => {
          // Force bypass the dedup guard so the list always refreshes
          lastFetchedRef.current = null;
          fetchData(); // fire-and-forget background refresh

          // Open the edit modal for the new member — fetch directly to avoid stale closure
          if (memberId) {
            try {
              const { data: { session } } = await supabase.auth.getSession();
              if (session?.access_token) {
                const response = await fetch(`${API_BASE_URL}/api/members/${memberId}`, {
                  headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json'
                  }
                });
                if (response.ok) {
                  const data = await response.json();
                  setEditingMember(data.data);
                  setIsReadOnlyView(false);
                  setShowEditModal(true);
                }
              }
            } catch (error) {
              console.error('Error fetching new member:', error);
            }
          }
        }}
      />

      {/* Edit Member Modal */}
      <EditMemberModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingMember(null);
          setIsReadOnlyView(false);
        }}
        member={editingMember as any}
        onMemberUpdated={handleMemberUpdated}
        readOnly={isReadOnlyView}
      />

      {/* Pause Modal */}
      {
        membershipToPause && (
          <PauseModal
            isOpen={showPauseModal}
            onClose={() => {
              setShowPauseModal(false);
              setMembershipToPause(null);
            }}
            membershipId={membershipToPause}
            onSuccess={() => {
              fetchData();
              setShowPauseModal(false);
              setMembershipToPause(null);
            }}
          />
        )
      }

      {/* Photo Viewer Modal */}
      {viewingPhoto && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4 animate-in fade-in duration-200"
          {...backdropClose(() => setViewingPhoto(null))}
        >
          <button 
            className="absolute top-6 right-6 p-2 text-white/50 hover:text-white transition-colors"
            onClick={() => setViewingPhoto(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={viewingPhoto} 
            alt="Full size profile" 
            className="max-w-full max-h-full rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Member Progress Modal */}
      {selectedMemberIdForProgress && (
        <MemberProgressGymModal
          isOpen={showProgressModal}
          onClose={() => {
            setShowProgressModal(false);
            setSelectedMemberIdForProgress(null);
          }}
          memberId={selectedMemberIdForProgress}
        />
      )}
      {/* Trainer-side workout plan builder */}
      {planPanelMember && (
        <MemberWorkoutPlanPanel
          memberId={planPanelMember.id}
          memberName={planPanelMember.name}
          onClose={() => setPlanPanelMember(null)}
        />
      )}
      {/* Trainer-side view of what the member actually logged */}
      {logPanelMember && (
        <MemberWorkoutLogPanel
          memberId={logPanelMember.id}
          memberName={logPanelMember.name}
          onClose={() => setLogPanelMember(null)}
        />
      )}
      {/* Quick-add purchase from member row */}
      <CreatePurchaseModal
        isOpen={showQuickPurchaseModal}
        onClose={() => setShowQuickPurchaseModal(false)}
        onSuccess={() => { setShowQuickPurchaseModal(false); fetchData(undefined, true); }}
        memberId={quickPurchaseMemberId}
        memberName={quickPurchaseMemberName}
      />
    </div >
  );
}